import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const PAGE_SIZE = 30;

export function useNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(false);

  const fetchPage = useCallback(async (offset) => {
    const { data, error: rpcError } = await supabase.rpc('get_notifications', {
      p_limit: PAGE_SIZE,
      p_offset: offset,
    });

    if (rpcError) {
      console.error('useNotifications:', rpcError);
      return null;
    }

    return data ?? [];
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const data = await fetchPage(0);
      if (cancelled) return;
      if (data === null) {
        setError(true);
      } else {
        setNotifications(data);
        setHasMore(data.length === PAGE_SIZE);
      }
      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [fetchPage]);

  const fetchMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);

    const data = await fetchPage(notifications.length);
    if (data !== null) {
      setNotifications(prev => [...prev, ...data]);
      setHasMore(data.length === PAGE_SIZE);
    }
    setLoadingMore(false);
  }, [fetchPage, notifications.length, loadingMore, hasMore]);

  // Optimistic mark-read (useVideoEngagement pattern: flip → rpc → revert on error)
  const markRead = useCallback(async (id) => {
    const stamp = new Date().toISOString();
    setNotifications(prev => prev.map(n => (n.id === id && !n.read_at ? { ...n, read_at: stamp } : n)));
    const { error: rpcError } = await supabase.rpc('mark_notifications_read', { p_ids: [id] });
    if (rpcError) {
      console.error('mark_notifications_read:', rpcError);
      setNotifications(prev => prev.map(n => (n.id === id ? { ...n, read_at: null } : n)));
    }
  }, []);

  const markAllRead = useCallback(async () => {
    const stamp = new Date().toISOString();
    setNotifications(prev => prev.map(n => (n.read_at ? n : { ...n, read_at: stamp })));
    const { error: rpcError } = await supabase.rpc('mark_notifications_read', { p_ids: null });
    if (rpcError) {
      console.error('mark_notifications_read (all):', rpcError);
      // Revert is ambiguous here — refetch page 0 instead
      const data = await fetchPage(0);
      if (data !== null) setNotifications(data);
    }
  }, [fetchPage]);

  return { notifications, loading, loadingMore, hasMore, error, fetchMore, markRead, markAllRead };
}
